import axios from 'axios';
import {
  SearchFormData, SearchResult, LoginRequest, SignupRequest, AuthResponse, User,
  UpdateProfileRequest, ChangePasswordRequest, InviteUserRequest, UserWithStats, UsageStats,
  BatchRowInput, BatchJobSummary, BatchJobDetail,
  CreateViewRequest, ProspectViewSummary, ProspectViewDetail,
  ViewEnrichmentJobStatus,
  AIPromptSummary, AIPromptVersion, AIModelOption, AIPromptTestResult, AICredits, AICacheStats, AIAuditEntry,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle 401 errors (expired/invalid tokens)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data from localStorage when token is invalid/expired
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      
      // Reload page to show login screen
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message || 'Login failed';
        throw new Error(message);
      }
      throw new Error('An unexpected error occurred during login');
    }
  },

  signup: async (userData: SignupRequest): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post('/auth/signup', userData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message || 'Signup failed';
        throw new Error(message);
      }
      throw new Error('An unexpected error occurred during signup');
    }
  },

  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await apiClient.get('/auth/me');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message || 'Failed to get user info';
        throw new Error(message);
      }
      throw new Error('An unexpected error occurred');
    }
  },

  resetPassword: async (token: string, newPassword: string, email?: string): Promise<{ message: string }> => {
    try {
      if (email) {
        // Email-based reset (new approach)
        const response = await apiClient.post('/auth/forgot-password', {
          email,
          new_password: newPassword
        });
        return response.data;
      } else {
        // Token-based reset (legacy approach) - not implemented in backend yet
        throw new Error('Token-based password reset is not currently supported');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message || 'Failed to reset password';
        throw new Error(message);
      }
      throw new Error('An unexpected error occurred');
    }
  },
};

// Search API (now protected)
export const searchKnowledgeCore = async (formData: SearchFormData): Promise<SearchResult> => {
  try {
    const response = await apiClient.post('/search', formData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'API request failed';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred');
  }
};

// Phone Validation API
export const validatePhoneNumbers = async (formData: SearchFormData): Promise<any> => {
  try {
    const response = await apiClient.post('/validate-phone', formData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Phone validation failed';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred during phone validation');
  }
};

// Email Validation API
export const validateEmailAddress = async (formData: SearchFormData): Promise<any> => {
  try {
    const response = await apiClient.post('/validate-email', formData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Email validation failed';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred during email validation');
  }
};

// AI Insights API
export const generateAIInsights = async (category: string, profileData: any): Promise<any> => {
  try {
    const response = await apiClient.post('/ai-insights', {
      category: category,
      profile_data: profileData
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'AI insights generation failed';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred during AI insights generation');
  }
};

// Recent Searches API
export const getRecentSearches = async (): Promise<any> => {
  try {
    const response = await apiClient.get('/recent/searches');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch recent searches';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred while fetching recent searches');
  }
};

export const clearRecentSearches = async (): Promise<any> => {
  try {
    const response = await apiClient.delete('/recent/searches/clear');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Failed to clear recent searches';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred while clearing recent searches');
  }
};

export const deleteSelectedSearches = async (searchIds: number[]): Promise<any> => {
  try {
    const response = await apiClient.delete('/recent/searches/delete', {
      data: { search_ids: searchIds }
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Failed to delete selected searches';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred while deleting selected searches');
  }
};

// Transaction History API
export const getTransactions = async (constituentId: string): Promise<any> => {
  try {
    const response = await apiClient.get(`/transactions/${constituentId}`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch transactions';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred while fetching transactions');
  }
};

// DataIris Search API
export const searchDataIris = async (formData: SearchFormData): Promise<any> => {
  try {
    const response = await apiClient.post('/datairis/search', formData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'DataIris search failed';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred during DataIris search');
  }
};

// DataIris Health Check API
export const checkDataIrisHealth = async (): Promise<any> => {
  try {
    const response = await apiClient.get('/datairis/health');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'DataIris health check failed';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred during DataIris health check');
  }
};

// BrightData Philanthropy API
export const getPhilanthropy = async (donorName: string, city: string, state: string): Promise<any> => {
  try {
    const response = await apiClient.post('/philanthropy/contributions', null, {
      params: {
        donor_name: donorName,
        city: city,
        state: state
      },
      timeout: 15 * 60 * 1000 // 15 minutes timeout for BrightData processing
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch philanthropy data';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred while fetching philanthropy data');
  }
};

// ── Profile / Account Management API ─────────────────────────────────────────

export const profileAPI = {
  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    try {
      const response = await apiClient.put('/auth/profile', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to update profile');
      throw new Error('An unexpected error occurred');
    }
  },

  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    try {
      await apiClient.put('/auth/password', data);
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to change password');
      throw new Error('An unexpected error occurred');
    }
  },

  listUsers: async (): Promise<UserWithStats[]> => {
    try {
      const response = await apiClient.get('/auth/users');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to fetch users');
      throw new Error('An unexpected error occurred');
    }
  },

  inviteUser: async (data: InviteUserRequest): Promise<User> => {
    try {
      const response = await apiClient.post('/auth/users/invite', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          // FastAPI 422 validation errors — extract human-readable messages
          const msg = detail.map((e: any) => `${e.loc?.slice(-1)[0] ?? 'field'}: ${e.msg}`).join('; ');
          throw new Error(msg);
        }
        throw new Error(typeof detail === 'string' ? detail : 'Failed to invite user');
      }
      throw new Error('An unexpected error occurred');
    }
  },

  getUsageStats: async (): Promise<UsageStats> => {
    try {
      const response = await apiClient.get('/auth/stats');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to fetch stats');
      throw new Error('An unexpected error occurred');
    }
  },

  setUserActive: async (userId: number, isActive: boolean): Promise<void> => {
    try {
      await apiClient.put(`/auth/users/${userId}/toggle-status`, { is_active: isActive });
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to update user status');
      throw new Error('An unexpected error occurred');
    }
  },
};

// ── AI Admin API ─────────────────────────────────────────────────────────────

const aiAdminError = (error: unknown, fallback: string): never => {
  if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || fallback);
  throw new Error('An unexpected error occurred');
};

const enc = (category: string) => encodeURIComponent(category);

export const aiAdminAPI = {
  listPrompts: async (): Promise<AIPromptSummary[]> => {
    try { return (await apiClient.get('/ai-admin/prompts')).data; }
    catch (e) { return aiAdminError(e, 'Failed to load prompts'); }
  },

  listPromptVersions: async (category: string): Promise<AIPromptVersion[]> => {
    try { return (await apiClient.get(`/ai-admin/prompts/${enc(category)}/versions`)).data; }
    catch (e) { return aiAdminError(e, 'Failed to load versions'); }
  },

  testPrompt: async (category: string, body: {
    prompt_text: string; model_id: string; first_name: string; last_name: string; city: string; state: string;
    profile_data?: Record<string, unknown>;
  }): Promise<AIPromptTestResult> => {
    try { return (await apiClient.post(`/ai-admin/prompts/${enc(category)}/test`, body)).data; }
    catch (e) { return aiAdminError(e, 'Test run failed'); }
  },

  publishPrompt: async (category: string, body: { prompt_text: string; note: string | null }) => {
    try { return (await apiClient.post(`/ai-admin/prompts/${enc(category)}/publish`, body)).data; }
    catch (e) { return aiAdminError(e, 'Failed to publish prompt'); }
  },

  rollbackPrompt: async (category: string, versionId: number) => {
    try { return (await apiClient.post(`/ai-admin/prompts/${enc(category)}/rollback/${versionId}`)).data; }
    catch (e) { return aiAdminError(e, 'Failed to roll back prompt'); }
  },

  deletePromptVersion: async (category: string, versionId: number) => {
    try { return (await apiClient.delete(`/ai-admin/prompts/${enc(category)}/versions/${versionId}`)).data; }
    catch (e) { return aiAdminError(e, 'Failed to delete prompt version'); }
  },

  listModels: async (): Promise<{ models: AIModelOption[]; active_model: string | null }> => {
    try { return (await apiClient.get('/ai-admin/models')).data; }
    catch (e) { return aiAdminError(e, 'Failed to load models'); }
  },

  setActiveModel: async (modelId: string) => {
    try { return (await apiClient.put('/ai-admin/models/active', { model_id: modelId })).data; }
    catch (e) { return aiAdminError(e, 'Failed to set active model'); }
  },

  getCredits: async (): Promise<AICredits> => {
    try { return (await apiClient.get('/ai-admin/credits')).data; }
    catch (e) { return aiAdminError(e, 'Failed to load credits'); }
  },

  getCacheStats: async (): Promise<AICacheStats> => {
    try { return (await apiClient.get('/ai-admin/cache/stats')).data; }
    catch (e) { return aiAdminError(e, 'Failed to load cache details'); }
  },

  clearCache: async (confirm: string): Promise<{ deleted_rows: number }> => {
    try { return (await apiClient.post('/ai-admin/cache/clear', { confirm })).data; }
    catch (e) { return aiAdminError(e, 'Failed to clear cache'); }
  },

  getAuditLog: async (): Promise<AIAuditEntry[]> => {
    try { return (await apiClient.get('/ai-admin/audit-log')).data; }
    catch (e) { return aiAdminError(e, 'Failed to load audit log'); }
  },

  setUserAiAdmin: async (userId: number, enabled: boolean) => {
    try { return (await apiClient.put(`/ai-admin/users/${userId}/ai-admin`, { enabled })).data; }
    catch (e) { return aiAdminError(e, 'Failed to update AI admin access'); }
  },
};

// ── Batch API ─────────────────────────────────────────────────────────────────

export const batchAPI = {
  submit: async (fileName: string, rows: BatchRowInput[]): Promise<{ job_id: number; status: string; total_rows: number }> => {
    try {
      const response = await apiClient.post('/batch/search', { file_name: fileName, rows });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to submit batch job');
      throw new Error('An unexpected error occurred');
    }
  },

  listJobs: async (): Promise<BatchJobSummary[]> => {
    try {
      const response = await apiClient.get('/batch/jobs');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to list batch jobs');
      throw new Error('An unexpected error occurred');
    }
  },

  getJob: async (jobId: number): Promise<BatchJobDetail> => {
    try {
      const response = await apiClient.get(`/batch/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to get job status');
      throw new Error('An unexpected error occurred');
    }
  },
};

export const viewsAPI = {
  create: async (body: CreateViewRequest): Promise<{ view_id: number }> => {
    try {
      const response = await apiClient.post('/views', body);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to create view');
      throw new Error('An unexpected error occurred');
    }
  },

  listViews: async (): Promise<ProspectViewSummary[]> => {
    try {
      const response = await apiClient.get('/views');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to list views');
      throw new Error('An unexpected error occurred');
    }
  },

  getView: async (viewId: number): Promise<ProspectViewDetail> => {
    try {
      const response = await apiClient.get(`/views/${viewId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to get view');
      throw new Error('An unexpected error occurred');
    }
  },

  deleteView: async (viewId: number): Promise<void> => {
    try {
      await apiClient.delete(`/views/${viewId}`);
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to delete view');
      throw new Error('An unexpected error occurred');
    }
  },

  startEnrichment: async (viewId: number, memberIds: number[]): Promise<{ job_id: number; status: string; total_rows: number }> => {
    try {
      const response = await apiClient.post(`/views/${viewId}/enrich`, { member_ids: memberIds });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to start enrichment');
      throw new Error('An unexpected error occurred');
    }
  },

  getEnrichmentStatus: async (viewId: number, jobId: number): Promise<ViewEnrichmentJobStatus> => {
    try {
      const response = await apiClient.get(`/views/${viewId}/enrich/${jobId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) throw new Error(error.response?.data?.detail || 'Failed to get enrichment status');
      throw new Error('An unexpected error occurred');
    }
  },

  getActiveEnrichment: async (viewId: number): Promise<{ id: number; status: string; total_rows: number; completed_rows: number; failed_rows: number } | null> => {
    try {
      const response = await apiClient.get(`/views/${viewId}/enrich/active`);
      return response.data.job;
    } catch {
      return null;
    }
  },
};

export const getLinkedIn = async (firstName: string, lastName: string, city: string, state: string): Promise<any> => {
  try {
    const response = await apiClient.get('/linkedin/profile', {
      params: {
        first_name: firstName,
        last_name: lastName,
        city: city,
        state: state
      },
      timeout: 5 * 60 * 1000 // 5 minutes timeout for BrightData LinkedIn polling
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message || 'Failed to fetch LinkedIn data';
      throw new Error(message);
    }
    throw new Error('An unexpected error occurred while fetching LinkedIn data');
  }
};